import {
  DeliveryTaskDoc,
  IssueProduct,
  IssueTask,
  IssueTaskDoc,
  OrderDoc,
  OrderProduct,
  PickupTask,
  PickupTaskDoc,
  StoreIssueDoc,
  TaskItem,
  TaskItemStatus,
  TaskOrderCollection,
} from '@gooduncles/gu-app-schema';
import { message, notification } from 'antd';
import { Timestamp } from 'firebase/firestore';

import { errorObjectToString } from 'src/utils/common';
import { formatDate } from 'src/utils/date-util';
import {
  batchEnd,
  batchStart,
  getDeliveryTaskForOrder,
  getIssueTask,
  getOrder,
  getPartnersUser,
  getPickupTaskForOrder,
  getStoreIssue,
  updateDeliveryTask,
  updateIssueTaskNestedFields,
  updateOrder,
  updatePickupTask,
  updateStoreIssue,
} from 'src/utils/firebase/firebase-short-cut';
import { AuditLogger } from 'src/utils/audit-logger';
import { TaskSpot } from 'src/utils/partners-util';

const auditLogger = AuditLogger.getInstance();

/**
 * TaskOrderCollection에 따라 주문, 이슈, 또는 IssueTask를 가져옵니다.
 * FIXME: issue migration ✅
 */
const getTaskOrder = async (
  orderCollection: TaskOrderCollection,
  orderId: string,
): Promise<OrderDoc | StoreIssueDoc | IssueTaskDoc | undefined> => {
  switch (orderCollection) {
    case TaskOrderCollection.Order:
      return await getOrder(orderId);
    case TaskOrderCollection.StoreIssue:
      return await getStoreIssue(orderId);
    case TaskOrderCollection.IssueTask:
      return await getIssueTask(orderId);
    default:
      throw new Error(`Unknown order collection: ${orderCollection}`);
  }
};

/**
 * TaskOrderCollection에 따라 주문, 이슈, 또는 IssueTask를 업데이트하고 성공 메시지를 출력합니다.
 * FIXME: issue migration ✅
 */
const updateTaskOrder = async (
  orderCollection: TaskOrderCollection,
  orderId: string,
  updateData: Partial<OrderDoc | StoreIssueDoc | IssueTask['pickupInfo'] | IssueTask['deliveryInfo']>,
  taskType: 'pickup' | 'delivery',
  useBatch = true,
): Promise<void> => {
  switch (orderCollection) {
    case TaskOrderCollection.Order:
      await updateOrder(orderId, updateData as Partial<OrderDoc>, { bBatch: useBatch });
      message.success(`주문의 ${taskType === 'pickup' ? '픽업' : '배송'} 담당자 변경.`);
      break;
    case TaskOrderCollection.StoreIssue:
      await updateStoreIssue(orderId, updateData as Partial<StoreIssueDoc>, { bBatch: useBatch });
      message.success(`이슈의 ${taskType === 'pickup' ? '픽업' : '배송'} 담당자 변경.`);
      break;
    case TaskOrderCollection.IssueTask:
      if (taskType === 'pickup') {
        await updateIssueTaskNestedFields(
          orderId,
          {
            pickupInfo: updateData as Partial<IssueTask['pickupInfo']>,
          },
          { bBatch: useBatch },
        );
      } else {
        await updateIssueTaskNestedFields(
          orderId,
          {
            deliveryInfo: updateData as Partial<IssueTask['deliveryInfo']>,
          },
          { bBatch: useBatch },
        );
      }
      message.success(`IssueTask의 ${taskType === 'pickup' ? '픽업' : '배송'} 담당자 변경.`);
      break;
    default:
      throw new Error(`Unknown order collection: ${orderCollection}`);
  }
};

/**
 * spot에 할당된 taskBundle을 order, storeIssue, issueTask로 분리한다.
 * FIXME: issue migration ✅
 */
export const separateTaskSpotToOrder = (
  taskSpot: TaskSpot,
): { orders: OrderDoc[]; storeIssues: StoreIssueDoc[]; issueTasks: IssueTaskDoc[] } => {
  const taskBundles = taskSpot.stores.flatMap((s) => s.taskBundles);
  return taskBundles.reduce<{
    orders: OrderDoc[];
    storeIssues: StoreIssueDoc[];
    issueTasks: IssueTaskDoc[];
  }>(
    (acc, cur) => {
      if (cur.orderColleciton === TaskOrderCollection.Order) {
        acc.orders.push(cur.data as OrderDoc);
      }
      if (cur.orderColleciton === TaskOrderCollection.StoreIssue) {
        acc.storeIssues.push(cur.data as StoreIssueDoc);
      }
      if (cur.orderColleciton === TaskOrderCollection.IssueTask) {
        acc.issueTasks.push(cur.data as IssueTaskDoc);
      }
      return acc;
    },
    {
      orders: [],
      storeIssues: [],
      issueTasks: [],
    },
  );
};

/**
 * 픽업 업무를 수동으로 완료처리합니다.
 */
export const finishPickupTask = async (pickupTask: PickupTaskDoc) => {
  const finishedPickupItems = Object.fromEntries(
    Object.entries(pickupTask.pickupItems).map(([key, value]) => [
      key,
      { ...value, itemStatus: TaskItemStatus.Completed },
    ]),
  );
  const finishedAt = formatDate(Timestamp.now().toDate(), "yyyy-MM-dd'T'HH:mm:ss+0900");
  const updateData: Partial<PickupTask> = {
    finishedAt,
    pickupItems: finishedPickupItems,
  };
  await updatePickupTask(pickupTask._id, updateData, { bBatch: true });
};

/**
 * 픽업 업무의 담당 파트너를 변경합니다.
 * FIXME: issue migration ✅
 */
export const changePickupTaskPartner = async (pickupTask: PickupTaskDoc, to: string) => {
  try {
    // 1. 업무를 받을 파트너의 상태를 확인합니다.
    const partner = await getPartnersUser(to);
    if (!partner) {
      throw new Error('파트너 정보를 찾을 수 없습니다.');
    }

    // 2. 연관된 주문 또는 이슈를 가져옵니다.
    if (pickupTask.orderColleciton === TaskOrderCollection.PurchaseOrder) {
      throw new Error('매입 주문은 파트너 변경이 불가능합니다.');
    }

    const taskOrder = await getTaskOrder(pickupTask.orderColleciton, pickupTask.orderId);
    if (!taskOrder) {
      throw new Error('연관된 주문 또는 이슈를 찾을 수 없습니다.');
    }

    // 3. 파트너 변경
    batchStart();
    // 3-1. 업무 담당 파트너 변경 (완료 시점 기사 닉네임 스냅샷은 틀려지므로 초기화)
    await updatePickupTask(pickupTask._id, { partnerId: partner._id }, { bBatch: true });
    // 3-2. 주문 또는 이슈 담당 파트너 변경
    await updateTaskOrder(
      pickupTask.orderColleciton,
      taskOrder._id,
      {
        pickupPartnerId: partner._id,
        pickupPartnerName: partner.name,
      },
      'pickup',
      true,
    );
    await batchEnd();
  } catch (error) {
    console.error(error);
    const description = errorObjectToString(error);
    notification.error({
      message: '픽업 업무 파트너 변경 오류',
      description,
    });
    auditLogger.log({
      action: 'UPDATE',
      entity: 'delivery',
      entityId: pickupTask._id,
      summary: `픽업 업무 파트너 변경에서 오류가 발생했습니다.\n${description}`,
      level: 'error',
    });
  }
};

/**
 * 배송 업무의 담당 파트너를 변경합니다.
 * FIXME: issue migration ✅
 */
export const changeDeliveryTaskPartner = async (deliveryTask: DeliveryTaskDoc, to: string) => {
  try {
    // 1. 업무를 받을 파트너의 상태를 확인합니다.
    const partner = await getPartnersUser(to);
    if (!partner) {
      throw new Error('파트너 정보를 찾을 수 없습니다.');
    }

    // 2. 연관된 주문 또는 이슈를 가져옵니다.
    if (deliveryTask.orderColleciton === TaskOrderCollection.PurchaseOrder) {
      throw new Error('매입 주문은 파트너 변경이 불가능합니다.');
    }

    const taskOrder = await getTaskOrder(deliveryTask.orderColleciton, deliveryTask.orderId);
    if (!taskOrder) {
      throw new Error('연관된 주문 또는 이슈를 찾을 수 없습니다.');
    }

    // 3. 파트너 변경
    batchStart();
    // 3-1. 업무 담당 파트너 변경 (완료 시점 기사 닉네임 스냅샷은 틀려지므로 초기화)
    await updateDeliveryTask(deliveryTask._id, { partnerId: partner._id, partnerNickname: null }, { bBatch: true });
    // 3-2. 주문 또는 이슈 담당 파트너 변경
    await updateTaskOrder(
      deliveryTask.orderColleciton,
      taskOrder._id,
      {
        deliveryPartnerId: partner._id,
        deliveryPartnerName: partner.name,
      },
      'delivery',
      true,
    );
    await batchEnd();
  } catch (error) {
    console.error(error);
    const description = errorObjectToString(error);
    notification.error({
      message: '배송 업무 파트너 변경 오류',
      description,
    });
    auditLogger.log({
      action: 'UPDATE',
      entity: 'delivery',
      entityId: deliveryTask._id,
      summary: `배송 업무 파트너 변경에서 오류가 발생했습니다.\n${description}`,
      level: 'error',
    });
  }
};

/**
 * 주문 정보로 업무를 조회합니다.
 */
export const getTasksForOrder = async (order: OrderDoc) => {
  const pickupTask = await getPickupTaskForOrder(order);
  const deliveryTask = await getDeliveryTaskForOrder(order);
  return { pickupTask, deliveryTask };
};

/**
 * 주문 상품을 업무로 변경합니다.
 */
export const convertOrderProductsToTaskItems = (orderProducts: OrderProduct[]): Record<string, TaskItem> => {
  return Object.fromEntries(
    orderProducts.map((orderProduct) => [
      orderProduct.productId,
      {
        productId: orderProduct.productId,
        itemStatus: TaskItemStatus.Assigned,
        issue: null,
        inventoryId: orderProduct.inventoryId ?? null,
      },
    ]),
  );
};

/**
 * IssueTask 상품을 업무로 변경합니다.
 * FIXME: issue migration ✅
 */
export const convertIssueTaskProductsToTaskItems = (issueProducts: IssueProduct[]): Record<string, TaskItem> => {
  return Object.fromEntries(
    issueProducts.map((issueProduct) => [
      issueProduct.productId,
      {
        productId: issueProduct.productId,
        itemStatus: TaskItemStatus.Assigned,
        issue: null,
        inventoryId: issueProduct.inventoryId ?? null,
      },
    ]),
  );
};
